import React, { useCallback, useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteIcon from "@mui/icons-material/Delete";

import { api } from "../../../../api/apiClient";
import { notify } from "../../../components-ui/toast";
import {
  ServiceEnrollmentRoleDto,
  ServiceInstanceRotaDto,
} from "../../../types/types";

import "../../../../css/cards.css";
import "../../../../css/forms.css";
import "../../../../css/buttons.css";

interface ServiceInstanceRotaCardProps {
  serviceId: number;
  serviceInstanceId: number;
}

export default function ServiceInstanceRotaCard({
  serviceId,
  serviceInstanceId,
}: ServiceInstanceRotaCardProps) {
  const [rota, setRota] = useState<ServiceInstanceRotaDto[]>([]);
  const [eligible, setEligible] = useState<ServiceEnrollmentRoleDto[]>([]);
  const [selectedEnrollmentRoleId, setSelectedEnrollmentRoleId] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [rotaResult, eligibleResult] = await Promise.all([
      api<ServiceInstanceRotaDto[]>(`/api/service-instances/${serviceInstanceId}/rota`),
      api<ServiceEnrollmentRoleDto[]>(`/api/services/${serviceId}/eligible-volunteers`),
    ]);

    setRota(rotaResult ?? []);
    setEligible(eligibleResult ?? []);
  }, [serviceId, serviceInstanceId]);

  useEffect(() => {
    load();
  }, [load]);

  const alreadyAssigned = useMemo(
    () => new Set(rota.map((item) => item.serviceEnrollmentRoleId)),
    [rota],
  );

  const availableEligible = useMemo(
    () =>
      eligible.filter(
        (item) => item.isActive && !alreadyAssigned.has(item.serviceEnrollmentRoleId),
      ),
    [alreadyAssigned, eligible],
  );

  const groupedRota = useMemo(() => {
    return rota.reduce<Record<string, ServiceInstanceRotaDto[]>>((groups, item) => {
      groups[item.serviceRoleName] = groups[item.serviceRoleName] ?? [];
      groups[item.serviceRoleName].push(item);
      return groups;
    }, {});
  }, [rota]);

  const addAssignment = async () => {
    const serviceEnrollmentRoleId = Number(selectedEnrollmentRoleId);
    if (!serviceEnrollmentRoleId) return;

    try {
      setSaving(true);
      await api<ServiceInstanceRotaDto>(
        `/api/service-instances/${serviceInstanceId}/rota`,
        {
          method: "POST",
          body: { serviceEnrollmentRoleId },
        },
      );
      notify.success("Volunteer added to rota.");
      setSelectedEnrollmentRoleId("");
      await load();
    } catch (error) {
      console.error("Failed to add rota assignment", error);
      notify.error("Failed to add volunteer.");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (
    serviceInstanceRotaId: number,
    status: "Confirmed" | "Cancelled",
  ) => {
    try {
      setSaving(true);
      await api<ServiceInstanceRotaDto>(
        `/api/service-instances/rota/${serviceInstanceRotaId}/status`,
        {
          method: "PUT",
          body: { status },
        },
      );
      await load();
    } catch (error) {
      console.error("Failed to update rota status", error);
      notify.error("Failed to update rota.");
    } finally {
      setSaving(false);
    }
  };

  const removeAssignment = async (serviceInstanceRotaId: number) => {
    try {
      setSaving(true);
      await api<void>(`/api/service-instances/rota/${serviceInstanceRotaId}`, {
        method: "DELETE",
      });
      await load();
    } catch (error) {
      console.error("Failed to remove rota assignment", error);
      notify.error("Failed to remove volunteer.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card" style={{ marginTop: 16 }}>
      <div className="card-header-row">
        <div>
          <div className="card-title">Planned Rota</div>
        </div>
      </div>

      <div className="form-row" style={{ alignItems: "end" }}>
        <div className="form-group">
          <label className="form-label">Eligible volunteer</label>
          <select
            className="form-input"
            value={selectedEnrollmentRoleId}
            onChange={(event) => setSelectedEnrollmentRoleId(event.target.value)}
            disabled={saving}
          >
            <option value="">Select volunteer role</option>
            {availableEligible.map((item) => (
              <option
                key={item.serviceEnrollmentRoleId}
                value={item.serviceEnrollmentRoleId}
              >
                {item.serviceRoleName} - {item.partyName ?? `Party ${item.partyId}`}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          className="action-button compact"
          onClick={addAssignment}
          disabled={saving || !selectedEnrollmentRoleId}
        >
          <AddIcon fontSize="small" />
          Add
        </button>
      </div>

      {Object.keys(groupedRota).length === 0 && (
        <div className="field-hint">No volunteers planned.</div>
      )}

      {Object.entries(groupedRota).map(([roleName, assignments]) => (
        <div key={roleName} style={{ marginTop: 12 }}>
          <div className="section-title">{roleName}</div>
          <div className="option-group">
            {assignments.map((assignment) => (
              <div
                key={assignment.serviceInstanceRotaId}
                className="service-enrollment-item"
              >
                <div>
                  <div className="service-enrollment-name">
                    {assignment.partyName}
                  </div>
                  <div className="field-hint" style={{ marginBottom: 0 }}>
                    {assignment.status}
                  </div>
                </div>
                <div className="frontline-grid-actions">
                  <button
                    type="button"
                    className="icon-button"
                    aria-label="Confirm rota assignment"
                    onClick={() =>
                      updateStatus(assignment.serviceInstanceRotaId, "Confirmed")
                    }
                    disabled={saving}
                  >
                    <CheckCircleIcon fontSize="small" />
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label="Cancel rota assignment"
                    onClick={() =>
                      updateStatus(assignment.serviceInstanceRotaId, "Cancelled")
                    }
                    disabled={saving}
                  >
                    <CancelIcon fontSize="small" />
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    aria-label="Remove rota assignment"
                    onClick={() => removeAssignment(assignment.serviceInstanceRotaId)}
                    disabled={saving}
                  >
                    <DeleteIcon fontSize="small" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
